import React, {useState} from 'react';

import find from 'lodash/find';

import {AttachOverlay} from 'app/general/overlays';
import {CheckboxButton} from 'app/general/widgets/CheckboxButton';
import {SimpleItemPickerListPopoverRTK} from 'app/general/widgets/SimpleItemPickerList';
import {
  convertLocationParamsToFetchParams,
  useInviteUser,
  useUsers,
} from 'app/user/api';
import {CreateUserPopover} from 'app/user/components/CreateUserForm';
import {gt} from 'app/utils/i18n';

import type {NewInvitationType} from 'app/user/types';
import type {UserSerializedType} from 'sweet-types';

type Props = {
  team: {id: number};
  title?: string;
  onConfirm?: (
    users: Array<UserSerializedType>,
    isManager: boolean
  ) => Promise<unknown>;
  className?: string;
};

export function AddNewTeamMemberButton({
  team,
  title,
  onConfirm,
  className,
}: Props) {
  const [giveManagerAccess, setManagerAccess] = useState<boolean>(false);
  const [inviteUser] = useInviteUser();

  const addUsersToTeam =
    onConfirm != null
      ? onConfirm
      : (users: Array<UserSerializedType>, isManager) => {
          const invitations = users.map<NewInvitationType>((user) => ({
            send_mail: false,
            content_type: 'team',
            permission: isManager ? 'edit' : 'view',
            object_id: team.id,
            to_user_id: user.url,
          }));
          return inviteUser({invitations});
        };

  const toggleManagerAccessCheckbox = (isChecked: boolean) => {
    setManagerAccess(isChecked);
  };

  const getUserItemProps = (user: UserSerializedType) => {
    return {
      disabledText:
        find(user.teams, {
          id: team.id,
        }) != null
          ? gt('Already team member')
          : undefined,
    };
  };

  return (
    <AttachOverlay
      trigger="click"
      overlay={({overlayProps}) => (
        <SimpleItemPickerListPopoverRTK
          showDescription={false}
          overlayProps={{...overlayProps, placement: 'left'}}
          additionalParams={{
            exclude_team_id: team.id,
            exclude_guests: '1',
          }}
          useDataHook={useUsers}
          paramsConverter={convertLocationParamsToFetchParams}
          addItemProps={getUserItemProps}
          onConfirm={(users) => addUsersToTeam(users, giveManagerAccess)}
          searchPlaceholder={gt('Search all members')}
          isPopover={true}
          isSmall={true}
        >
          {(selectedItems, refetchData, onChange) => (
            <div className="actions-block">
              {selectedItems.length > 0 ? (
                <CheckboxButton
                  className="btn btn-sm btn-link"
                  isChecked={giveManagerAccess}
                  onClick={toggleManagerAccessCheckbox}
                >
                  {gt('Give manager access')}
                </CheckboxButton>
              ) : null}
              <AttachOverlay
                trigger="click"
                overlay={({overlayProps}) => (
                  <CreateUserPopover
                    isPopover={true}
                    showTeams={false}
                    overlayProps={{...overlayProps, placement: 'right'}}
                    onCreateUser={(user) => {
                      onChange([...selectedItems, user]);
                      return Promise.resolve(true);
                    }}
                    hideGuestInvite={true}
                  />
                )}
              >
                {(triggers) => (
                  <button {...triggers} className="btn btn-sm btn-link">
                    {gt('Invite new member to account')}
                  </button>
                )}
              </AttachOverlay>
            </div>
          )}
        </SimpleItemPickerListPopoverRTK>
      )}
    >
      {(triggers) => (
        <button className={className ?? 'btn btn-light'} {...triggers}>
          <i className="fas fa-plus-circle mr-1" />
          {gt(title ?? 'Member')}
        </button>
      )}
    </AttachOverlay>
  );
}
